import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { useLazyQuery, useLazyCommand, useEvent } from '@soap/modules';
import { Pencil } from 'lucide-react';

const viewStates = {
    closed: 0,
    loading: 1,
    ready: 2,
    submitting: 3
};

interface ChangeAccountNameDialogProps {
    onNameUpdated?: (newName: string) => void;
}

export function ChangeAccountNameDialog({ onNameUpdated }: ChangeAccountNameDialogProps) {
    const [viewState, setViewState] = useState(viewStates.closed);
    const [accountName, setAccountName] = useState('');
    const [open, setOpen] = useState(false);
    const isSubmittingRef = useRef(false);
    
    const [getFormData] = useLazyQuery("C240v1_GetC239FormData");
    const [submitCommand] = useLazyCommand("C239v1_ChangeTenantName");

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setViewState(viewStates.closed);
            setAccountName('');
            isSubmittingRef.current = false;
        }
    }, [open]);

    // Fetch form data when dialog opens
    useEffect(() => {
        if (open && viewState === viewStates.closed) {
            setViewState(viewStates.loading);
            getFormData({});
        }
    }, [open, viewState, getFormData]);

    // Listen for form data event
    useEvent({
        eventName: "E192v1_GotC239FormData",
        onEventReceived: (event: any) => {
            const fieldData = event.e000_FieldData || [];
            const newNameField = fieldData.find((field: any) => field.name === 'c239_NewName');
            setAccountName(newNameField?.initialValue || '');
            setViewState(viewStates.ready);
        }
    }, []);

    // Listen for successful update event
    useEvent({
        eventName: "E194v1_OrganisationNameUpdated",
        onEventReceived: () => {
            if (isSubmittingRef.current) {
                // Call the callback with the new name before closing
                if (onNameUpdated && accountName.trim()) {
                    onNameUpdated(accountName.trim());
                }
                setOpen(false);
                isSubmittingRef.current = false;
            }
        }
    }, [onNameUpdated, accountName]);

    const close = useCallback(() => {
        setOpen(false);
        setViewState(viewStates.closed);
        setAccountName('');
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!accountName.trim() || accountName.trim().length < 2) return;
        
        setViewState(viewStates.submitting);
        isSubmittingRef.current = true;
        try {
            submitCommand({
                C239_NewName: accountName.trim()
            });
        } catch (error) {
            console.error('Failed to update account name:', error);
            setViewState(viewStates.ready);
            isSubmittingRef.current = false;
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 px-2">
                    <Pencil className="w-4 h-4 mr-2" />
                    Change Name
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Change Account Name</DialogTitle>
                </DialogHeader>
                
                {viewState === viewStates.loading && (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
                    </div>
                )}

                {viewState === viewStates.submitting && (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
                    </div>
                )}

                {viewState === viewStates.ready && (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="accountName" className="text-sm font-medium text-gray-700">
                                New Account Name
                            </Label>
                            <Input
                                id="accountName"
                                type="text"
                                placeholder="Enter account name"
                                value={accountName}
                                onChange={(e) => setAccountName(e.target.value)}
                                required
                                minLength={2}
                                className="w-full"
                                disabled={viewState === viewStates.submitting}
                            />
                            <p className="text-sm text-gray-500">
                                The account name must be at least 2 characters long.
                            </p>
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={close}
                                disabled={viewState === viewStates.submitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!accountName.trim() || accountName.trim().length < 2 || viewState === viewStates.submitting}
                                className="min-w-[100px]"
                            >
                                {viewState === viewStates.submitting ? "Saving..." : "Save"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

